/**
 * The Odoo model of the « Configuration » tab (`ConfigurationTab.tsx`):
 * the Odoo descriptor's configurable fields as the tab's shape
 * (`EnvironmentConfiguration`), its refusals in the tab's words
 * (`ConfigurationProblem`, `RULE_PROBLEMS`) and the engine's plan over a
 * draft (`planDraft`). Moved from the core's
 * `projectEnvironments/configuration.ts` at step 4 of the genericity plan
 * (2026-10-24), unchanged: the route's client, the references and the
 * launch stay there, shared with the generic tab.
 */

import {
  applyProfile as applyDescriptorProfile,
  lockedFieldIds,
  peakReservation as descriptorPeakReservation,
  planWithDescriptor,
  profileOf as descriptorProfileOf,
  type AppTypeDescriptor,
  type ConfigurationGrant,
  type DatabaseMode,
  type FieldValue,
  type FieldValues,
  type PlanProblem,
  type RangeBound,
  type Reservation,
} from '@internal/plugin-waadoo-config-common';
import {
  CUSTOM_PROFILE,
  type ConfigurationResponse,
  type DatabaseFields,
} from '@internal/plugin-waadoo-core';

export type { DatabaseMode };

/**
 * The Odoo descriptor's configurable fields, the tab's layout. A type alias,
 * not an interface: it is then a `FieldValues` for the engine.
 */
export type EnvironmentConfiguration = {
  workers: number;
  odooCpuRequest: number;
  odooCpuLimit: number;
  odooMemoryRequest: number;
  odooMemoryLimit: number;
  filestoreSize: number;
  dbCpuRequest: number;
  dbCpuLimit: number;
  dbMemoryRequest: number;
  dbMemoryLimit: number;
  databaseSize: number;
  haCache: boolean;
  haAutoscaling: boolean;
  haMinReplicas: number;
  haMaxReplicas: number;
  haTargetCpu: number;
  haScaleDownWindow: number;
  haDbMode: DatabaseMode;
  haDbSync: boolean;
};

export type ConfigurationField = keyof EnvironmentConfiguration;

/**
 * The panel's order: the configurable fields of the Odoo descriptor, in its
 * order (`@internal/plugin-waadoo-config-common`, `descriptor.ts`).
 */
export const CONFIGURATION_FIELDS: readonly ConfigurationField[] = [
  'workers',
  'odooCpuRequest',
  'odooCpuLimit',
  'odooMemoryRequest',
  'odooMemoryLimit',
  'dbCpuRequest',
  'dbCpuLimit',
  'dbMemoryRequest',
  'dbMemoryLimit',
  'filestoreSize',
  'databaseSize',
  'haAutoscaling',
  'haCache',
  'haMinReplicas',
  'haMaxReplicas',
  'haTargetCpu',
  'haScaleDownWindow',
  'haDbMode',
  'haDbSync',
];

/**
 * The PostgreSQL section's fields (`DatabaseSection` of the core): the roles of the Odoo
 * descriptor's `database` component (`databaseFieldsOf`, pinned by
 * `configuration.test.ts`).
 */
export const DATABASE_FIELDS: DatabaseFields<ConfigurationField> = {
  mode: 'haDbMode',
  sync: 'haDbSync',
  cpuRequest: 'dbCpuRequest',
  cpuLimit: 'dbCpuLimit',
  memoryRequest: 'dbMemoryRequest',
  memoryLimit: 'dbMemoryLimit',
  size: 'databaseSize',
};

export type RangeField = Exclude<
  ConfigurationField,
  'workers' | 'haCache' | 'haAutoscaling' | 'haDbMode' | 'haDbSync'
>;

/**
 * Odoo's bounds as the engine computes them (`configurationBounds`), its
 * fields named: the `workers` list, every range.
 */
export interface ConfigurationBounds {
  values: { workers: number[] };
  ranges: Record<RangeField, RangeBound>;
}

/**
 * The engine's values as the tab's configuration. The descriptor is Odoo's
 * (the tab's layout is), whose configurable fields are
 * `CONFIGURATION_FIELDS`, the plan writing every one of them: the one cast
 * between the engine's generic values and the tab's shape.
 */
function asConfiguration(values: FieldValues): EnvironmentConfiguration {
  return values as EnvironmentConfiguration;
}

const isConfigurationField = (id: string): id is ConfigurationField =>
  (CONFIGURATION_FIELDS as readonly string[]).includes(id);

/**
 * The fields an editor may not change: their current value is beyond the
 * creation form's bounds, an administrator's choice. None for an admin
 * (`lockedFieldIds` of the engine).
 */
export function lockedFields(
  descriptor: AppTypeDescriptor,
  current: EnvironmentConfiguration,
  access: ConfigurationResponse['access'],
): Set<ConfigurationField> {
  return new Set(
    [
      ...lockedFieldIds(
        descriptor,
        current,
        access === 'admin' ? 'admin' : 'editor',
      ),
    ].filter(isConfigurationField),
  );
}

/**
 * One problem of a draft: its key in `translation.ts` and its values; `text`
 * for a refusal the tab has no words for (a rule without `id`, or one it
 * does not know), said as the engine says it, with `fields`, those its
 * test reads (`PlanProblem.fields`): the tab marks them invalid.
 */
export type ConfigurationProblem =
  | {
      key:
        | 'locked'
        | 'bounds'
        | 'workers'
        | 'shrink'
        | 'requestAboveLimit'
        | 'maxBelowMin'
        | 'connections'
        | 'sharedBuffers';
      field: ConfigurationField;
      values?: Record<string, string | number>;
    }
  | {
      key: 'text';
      field?: undefined;
      fields: ConfigurationField[];
      text: string;
    };

const figure = (value: FieldValue | undefined): string | number =>
  typeof value === 'number' ? value : String(value);

/**
 * The refusals of the Odoo descriptor's rules, by `id` (platform-catalog
 * `apps/odoo/apptype.yaml`): the key of the tab's words, the field it is
 * said next to, its values from the two sides of the rule's comparison
 * (`PlanProblem`: `left` and `right`).
 */
const RULE_PROBLEMS: Readonly<
  Record<string, (params: PlanProblem['params']) => ConfigurationProblem>
> = {
  odooCpuRequestAboveLimit: () => ({
    key: 'requestAboveLimit',
    field: 'odooCpuRequest',
  }),
  odooMemoryRequestAboveLimit: () => ({
    key: 'requestAboveLimit',
    field: 'odooMemoryRequest',
  }),
  dbCpuRequestAboveLimit: () => ({
    key: 'requestAboveLimit',
    field: 'dbCpuRequest',
  }),
  dbMemoryRequestAboveLimit: () => ({
    key: 'requestAboveLimit',
    field: 'dbMemoryRequest',
  }),
  maxBelowMin: () => ({ key: 'maxBelowMin', field: 'haMaxReplicas' }),
  // `4 * … * haMaxReplicas <= 200`: the connections opened, the maximum.
  connections: params => ({
    key: 'connections',
    field: 'haMaxReplicas',
    values: { floor: figure(params.left), max: figure(params.right) },
  }),
  // `dbMemoryRequest >= floor(dbMemoryLimit / 4)`: shared_buffers.
  sharedBuffers: params => ({
    key: 'sharedBuffers',
    field: 'dbMemoryRequest',
    values: { buffers: figure(params.right) },
  }),
};

const RULE_KEY = 'rule:';

/** An engine refusal in the tab's terms (`ConfigurationProblem`). */
function problemOf(problem: PlanProblem): ConfigurationProblem {
  const text: ConfigurationProblem = {
    key: 'text',
    fields: (problem.fields ?? []).filter(isConfigurationField),
    text: problem.text,
  };
  const { key, params } = problem;
  if (key?.startsWith(RULE_KEY)) {
    const rule = key.slice(RULE_KEY.length);
    return Object.prototype.hasOwnProperty.call(RULE_PROBLEMS, rule)
      ? RULE_PROBLEMS[rule](params)
      : text;
  }
  const field = problem.field;
  if (field === undefined || !isConfigurationField(field)) {
    return text;
  }
  switch (key) {
    case 'locked':
      return { key: 'locked', field };
    case 'bounds':
      return {
        key: 'bounds',
        field,
        values: {
          min: figure(params.min),
          max: figure(params.max),
          step: figure(params.step),
        },
      };
    case 'values':
      return {
        key: 'workers',
        field,
        values: { allowed: figure(params.allowed) },
      };
    case 'growOnly':
      return {
        key: 'shrink',
        field,
        values: { current: figure(params.current) },
      };
    default:
      return text;
  }
}

export interface ConfigurationChange {
  field: ConfigurationField;
  before: EnvironmentConfiguration[ConfigurationField];
  after: EnvironmentConfiguration[ConfigurationField];
  derived: boolean;
}

export interface ConfigurationPlan {
  desired: EnvironmentConfiguration;
  changes: ConfigurationChange[];
  problems: ConfigurationProblem[];
  /** What to send: the fields the caller changed, as asked. */
  asked: Partial<EnvironmentConfiguration>;
}

/**
 * The engine's plan over a draft (`planWithDescriptor`, as
 * `waadoo:env:configure` runs it): the fields of `draft` that differ from
 * `current` are what is asked; the rules derive the rest.
 */
export function planDraft(input: {
  descriptor: AppTypeDescriptor;
  current: EnvironmentConfiguration;
  draft: EnvironmentConfiguration;
  grant: ConfigurationGrant;
}): ConfigurationPlan {
  const { current, draft } = input;
  const asked: Partial<EnvironmentConfiguration> = {};
  for (const field of CONFIGURATION_FIELDS) {
    if (draft[field] !== current[field]) {
      (asked as Record<string, unknown>)[field] = draft[field];
    }
  }
  const plan = planWithDescriptor({
    descriptor: input.descriptor,
    current,
    changes: asked,
    grant: input.grant,
  });
  const desired = asConfiguration(plan.desired);
  return {
    desired,
    changes: plan.changes
      .filter(
        (change): change is typeof change & { field: ConfigurationField } =>
          isConfigurationField(change.field),
      )
      .map(({ field, derived }) => ({
        field,
        before: current[field],
        after: desired[field],
        derived,
      })),
    problems: plan.problemDetails.map(problemOf),
    asked,
  };
}

/**
 * The descriptor's profile whose numbers the configuration holds, whatever
 * its volumes (`profileOf` of the engine), or `CUSTOM_PROFILE`.
 */
export function profileOf(
  descriptor: AppTypeDescriptor,
  config: EnvironmentConfiguration,
): string {
  return descriptorProfileOf(descriptor, config) ?? CUSTOM_PROFILE;
}

/**
 * `draft` with a profile's numbers, as the creation form's profile picker
 * writes them — but the volumes only grow (the larger of the current size
 * and the profile's), and a field locked for the caller keeps its value
 * (`applyProfile` of the engine).
 */
export function applyProfile(
  descriptor: AppTypeDescriptor,
  draft: EnvironmentConfiguration,
  current: EnvironmentConfiguration,
  profileId: string,
  locked: ReadonlySet<ConfigurationField>,
): EnvironmentConfiguration {
  return asConfiguration(
    applyDescriptorProfile(descriptor, { draft, current, profileId, locked }),
  );
}

/**
 * What the environment reserves at its peak — every Odoo pod of the HPA's
 * maximum, Valkey, every PostgreSQL instance —, in millicores and Mi
 * (`peakReservation` of the engine).
 */
export function peakReservation(
  descriptor: AppTypeDescriptor,
  config: EnvironmentConfiguration,
  grant: ConfigurationGrant,
): Reservation {
  return descriptorPeakReservation(descriptor, config, grant);
}
